import React from "react";
import { Col, Row, Button } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";

import SearchFilter from "../CustomUIComponents/SearchFilter/SearchFilter";
import TextFieldComponent from "../CustomUIComponents/TextFieldComponent/TextFieldComponent";
import {
  SEARCH_FILTER_TYPE,
  SEARCH_FILTER_FIELDS,
} from "../../constants/appConstants";
import { Strings } from "../../resources/i18n/i18n";
import { OPERATION_STATUS } from "../../config/axios.init";
import "./DatabaseTables.scss";
import {
  StyledHeadCell,
  StyledBodyCell,
  StyledRow,
  formatValue,
  PAYMENT_REVERSAL_TABS as TABS,
  fetchReversalByTab,
  deleteReversalByTab,
  updateReversalByTab,
  insertReversalByTab,
} from "./Constants";

// Timestamp columns the optional date filter matches against.
const DATE_MATCH_COLUMNS = ["created_at", "updated_at"];

// ── Component ────────────────────────────────────────────────────────────────

class PaymentReversal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 0,
      searchValues: {},
      rows: undefined, // undefined = not searched, null/[] = searched with no data
      loading: false,
      actionLoading: false,
      successMessage: "",
      errorMessage: "",
      confirmDialog: { open: false, id: null, label: "" },
      infoDialog: { open: false, rowData: null },
      // mode: "edit" | "add"
      formDialog: { open: false, mode: null, id: null, values: {} },
    };
  }

  getCurrentTab = () => TABS[this.state.activeTab];

  // ── Search ─────────────────────────────────────────────────────────────────

  handleSearchValueChange = (name, value) => {
    this.setState((prev) => ({
      searchValues: { ...prev.searchValues, [name]: value },
      errorMessage: "",
    }));
  };

  handleSearch = () => {
    const tab = this.getCurrentTab();
    const { searchValues } = this.state;
    const missing = tab.searchParams.some(
      (p) =>
        !String(
          searchValues[p.name] === undefined
            ? ""
            : searchValues[p.name]
        ).trim()
    );
    if (missing) {
      this.setState({
        errorMessage: Strings("reversalEnterSearchValues"),
      });
      return;
    }
    this.setState(
      {
        loading: true,
        successMessage: "",
        errorMessage: "",
        rows: undefined,
      },
      () => {
        fetchReversalByTab(tab, searchValues, (response) => {
          if (response.status === OPERATION_STATUS.SUCCESS) {
            this.setState({
              loading: false,
              rows: this.applyDateFilter(
                response.data.data,
                searchValues.date
              ),
            });
          } else {
            this.setState({
              loading: false,
              rows: null,
              errorMessage:
                response.error?.message ||
                Strings("errorFetchingData"),
            });
          }
        });
      }
    );
  };

  handleReset = () => {
    this.setState({
      searchValues: {},
      rows: undefined,
      loading: false,
      successMessage: "",
      errorMessage: "",
    });
  };

  handleTabChange = (index) => {
    // Keep searchValues so shared keys (loanProcessId / dailyStatusMessengerId / minimartId /
    // messengerDepositId / date …) don't need re-entering across tabs. Auto-fetch the new tab
    // when all of its required search params are already filled.
    const newTab = TABS[index];
    const { searchValues } = this.state;
    const hasAllParams = newTab.searchParams.every(
      (p) => String(searchValues[p.name] === undefined ? "" : searchValues[p.name]).trim()
    );
    this.setState(
      {
        activeTab: index,
        rows: undefined,
        successMessage: "",
        errorMessage: "",
      },
      () => {
        if (hasAllParams) {
          this.handleSearch();
        }
      }
    );
  };

  refetch = () => {
    const tab = this.getCurrentTab();
    const { searchValues } = this.state;
    fetchReversalByTab(tab, searchValues, (r) => {
      if (r.status === OPERATION_STATUS.SUCCESS) {
        this.setState({
          rows: this.applyDateFilter(r.data.data, searchValues.date),
        });
      }
    });
  };

  // Optional date filter — keeps rows whose created_at OR updated_at falls on the given date.
  applyDateFilter = (rows, date) => {
    if (!date || !String(date).trim() || !Array.isArray(rows)) {
      return rows;
    }
    const target = String(date).trim();
    return rows.filter((row) =>
      DATE_MATCH_COLUMNS.some((col) =>
        this.valueMatchesDate(row[col], target)
      )
    );
  };

  valueMatchesDate = (value, target) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === "number") {
      return this.toLocalYmd(new Date(value)) === target;
    }
    const text = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
      return text.slice(0, 10) === target;
    }
    const parsed = new Date(text);
    return (
      !Number.isNaN(parsed.getTime()) &&
      this.toLocalYmd(parsed) === target
    );
  };

  toLocalYmd = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  afterMutation = (response, successMsg, closeForm) => {
    if (response.status === OPERATION_STATUS.SUCCESS) {
      this.setState(
        {
          actionLoading: false,
          successMessage: successMsg,
          errorMessage: "",
          ...(closeForm
            ? {
                formDialog: {
                  open: false,
                  mode: null,
                  id: null,
                  values: {},
                },
              }
            : {}),
        },
        this.refetch
      );
    } else {
      this.setState({
        actionLoading: false,
        errorMessage:
          response.error?.message || Strings("errorFetchingData"),
      });
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────

  openDeleteDialog = (id) =>
    this.setState({
      confirmDialog: { open: true, id, label: `id: ${id}` },
    });

  closeDeleteDialog = () =>
    this.setState({
      confirmDialog: { open: false, id: null, label: "" },
    });

  confirmDelete = () => {
    const tab = this.getCurrentTab();
    const { id } = this.state.confirmDialog;
    this.setState({
      actionLoading: true,
      confirmDialog: { open: false, id: null, label: "" },
    });
    deleteReversalByTab(tab.key, id, (r) =>
      this.afterMutation(r, Strings("reversalDeleteSuccess"), false)
    );
  };

  // ── Edit / Add ─────────────────────────────────────────────────────────────

  // All columns editable except id / timestamps. Objects are shown as JSON text.
  openEditDialog = (row) => {
    const tab = this.getCurrentTab();
    const values = {};
    (tab.editableFields || []).forEach((field) => {
      const value = row[field.key];
      if (value === null || value === undefined) {
        values[field.key] = "";
      } else if (field.type === "date") {
        // HTML date inputs need yyyy-MM-dd
        const text = String(value);
        values[field.key] = /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
      } else if (typeof value === "object") {
        values[field.key] = JSON.stringify(value);
      } else {
        values[field.key] = value;
      }
    });
    this.setState({
      formDialog: {
        open: true,
        mode: "edit",
        id: row[tab.idField],
        values,
      },
    });
  };

  openAddDialog = () => {
    const tab = this.getCurrentTab();
    const values = {};
    (tab.insertFields || []).forEach((field) => {
      values[field.key] = "";
    });
    this.setState({
      formDialog: { open: true, mode: "add", id: null, values },
    });
  };

  closeFormDialog = () =>
    this.setState({
      formDialog: { open: false, mode: null, id: null, values: {} },
    });

  handleFormFieldChange = (key, value) => {
    this.setState((prev) => ({
      formDialog: {
        ...prev.formDialog,
        values: { ...prev.formDialog.values, [key]: value },
      },
    }));
  };

  submitForm = () => {
    const tab = this.getCurrentTab();
    const { mode, id, values } = this.state.formDialog;
    this.setState({ actionLoading: true });
    if (mode === "add") {
      const body = {
        ...values,
        loanProcessId: this.state.searchValues.loanProcessId,
      };
      insertReversalByTab(tab.key, body, (r) =>
        this.afterMutation(r, Strings("reversalInsertSuccess"), true)
      );
    } else {
      updateReversalByTab(tab.key, id, values, (r) =>
        this.afterMutation(r, Strings("reversalUpdateSuccess"), true)
      );
    }
  };

  // ── Info ───────────────────────────────────────────────────────────────────

  openInfo = (rowData) =>
    this.setState({ infoDialog: { open: true, rowData } });
  closeInfo = () =>
    this.setState({ infoDialog: { open: false, rowData: null } });

  // ── Renders ──────────────────────────────────────────────────────────────

  renderSearchFilter = () => {
    const tab = this.getCurrentTab();
    const { searchValues } = this.state;
    const filters = tab.searchParams.map((p) => ({
      type: SEARCH_FILTER_TYPE.INPUT,
      searchPlaceHolder: p.name,
      handleOnChange: (event) =>
        this.handleSearchValueChange(p.name, event.target.value),
      Value:
        searchValues[p.name] === undefined
          ? ""
          : searchValues[p.name],
      inputType:
        p.type === "number" ? SEARCH_FILTER_TYPE.NUMBER : "text",
      fieldname:
        p.type === "number"
          ? SEARCH_FILTER_FIELDS.MATRICES_ID
          : p.name,
      name: p.name,
      paddingTop: true,
      column: 3,
    }));

    // Optional cross-cutting date filter (matched client-side against created_at / updated_at).
    filters.push({
      type: SEARCH_FILTER_TYPE.INPUT,
      searchPlaceHolder: Strings("reversalDateLabel"),
      handleOnChange: (event) =>
        this.handleSearchValueChange("date", event.target.value),
      Value: searchValues.date === undefined ? "" : searchValues.date,
      inputType: "date",
      fieldname: "reversalDate",
      name: "date",
      paddingTop: true,
      column: 3,
    });

    return (
      <SearchFilter
        filters={filters}
        handleOnClickSearchFilter={this.handleSearch}
        handleOnClickResetFilter={this.handleReset}
        paddingRight={0}
        paddingTop={2}
        removeLeftPadding={true}
      />
    );
  };

  renderNote = (tab) => {
    if (!tab.note) {
      return null;
    }
    return (
      <Row className='mb-2'>
        <Col md={12}>
          <div className='reversalNote'>
            <InfoIcon className='reversalNoteIcon' />
            <div className='reversalNoteBody'>
              <div className='reversalNoteTitle'>
                {Strings("reversalNoteLabel")}
              </div>
              <div className='reversalNoteText'>
                {Strings(tab.note)}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  renderMessages = () => {
    const { successMessage, errorMessage } = this.state;
    return (
      <>
        {successMessage && (
          <Row className='mb-2 mt-2'>
            <Col>
              <div className='successMessage'>{successMessage}</div>
            </Col>
          </Row>
        )}
        {errorMessage && (
          <Row className='mb-2 mt-2'>
            <Col>
              <div className='errors p-2 errorMessage'>
                {errorMessage}
              </div>
            </Col>
          </Row>
        )}
      </>
    );
  };

  renderRowsTable = (rows, tab) => {
    if (!rows || rows.length === 0) {
      return (
        <div className='noRecordsFound noBranchRecords'>
          {Strings("NoRecordsFound")}
        </div>
      );
    }

    const idField = tab.idField;
    const keys = [
      idField,
      ...Object.keys(rows[0] || {}).filter((k) => k !== idField),
    ];
    const canEdit = !!tab.ops.update;
    const canDelete = !!tab.ops.del;

    return (
      <TableContainer
        component={Paper}
        sx={{ border: "1px solid #ccc", maxHeight: 400 }}
      >
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {keys.map((k) => (
                <StyledHeadCell key={k}>{k}</StyledHeadCell>
              ))}
              <StyledHeadCell align='center' sx={{ width: 120 }}>
                {Strings("actionsLabel")}
              </StyledHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <StyledRow
                key={row[idField] === undefined ? idx : row[idField]}
              >
                {keys.map((k) => (
                  <StyledBodyCell key={k} title={formatValue(row[k])}>
                    {formatValue(row[k])}
                  </StyledBodyCell>
                ))}
                <StyledBodyCell align='center'>
                  {canEdit && (
                    <Tooltip title={Strings("Edit")}>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={() => this.openEditDialog(row)}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title={Strings("Delete")}>
                      <IconButton
                        size='small'
                        color='error'
                        onClick={() =>
                          this.openDeleteDialog(row[idField])
                        }
                      >
                        <DeleteSweepIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={Strings("rowDetails")}>
                    <IconButton
                      size='small'
                      color='info'
                      onClick={() => this.openInfo(row)}
                    >
                      <InfoIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </StyledBodyCell>
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  renderTabContent = (tab) => {
    const { rows, loading } = this.state;

    if (loading) {
      return (
        <div className='text-center py-4'>
          <CircularProgress size={28} />
        </div>
      );
    }

    if (rows === undefined) {
      return (
        <div className='noRecordsFound noBranchRecords searchPrompt'>
          {Strings("reversalSearchPrompt")}
        </div>
      );
    }

    return (
      <>
        {tab.ops.insert && (
          <Row className='mb-2'>
            <Col xs='auto'>
              <Button
                size='sm'
                variant='success'
                onClick={this.openAddDialog}
              >
                <AddIcon
                  fontSize='small'
                  style={{ marginRight: 4, verticalAlign: "middle" }}
                />
                {Strings("reversalAddRow")}
              </Button>
            </Col>
          </Row>
        )}
        {this.renderRowsTable(rows, tab)}
      </>
    );
  };

  getFieldInputType = (field) => {
    if (field.type === "enum") {
      return SEARCH_FILTER_TYPE.SELECT;
    }
    if (field.type === "number") {
      return SEARCH_FILTER_TYPE.NUMBER;
    }
    if (field.type === "date") {
      return "date";
    }
    return "text";
  };

  // Keeps the current DB value selectable even if it is not in the known enum list.
  getEnumOptions = (field, currentValue) => {
    const options = field.options || [];
    if (currentValue && !options.includes(currentValue)) {
      return [currentValue, ...options];
    }
    return options;
  };

  renderFormField = (field) => {
    const value = this.state.formDialog.values[field.key];
    const isEnum = field.type === "enum";

    return (
      <Col xs={12} md={6} className="mb-3 px-2 reversalEditField" key={field.key}>
        <div className="reversalEditLabel">{field.key}</div>
        <TextFieldComponent
          id={`reversal-field-${field.key}`}
          type={this.getFieldInputType(field)}
          value={value === undefined || value === null ? "" : value}
          handleChange={(event) =>
            this.handleFormFieldChange(field.key, event.target.value)
          }
          halfWidth={false}
        >
          {isEnum && [
            <option key="__none" value="">
              {Strings("none")}
            </option>,
            ...this.getEnumOptions(field, value).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )),
          ]}
        </TextFieldComponent>
      </Col>
    );
  };

  renderFormDialog = () => {
    const tab = this.getCurrentTab();
    const { formDialog, actionLoading } = this.state;
    if (!formDialog.open) {
      return null;
    }
    const isAdd = formDialog.mode === "add";
    // Add: fixed insertFields. Edit: the tab's whitelisted editableFields (with enum types).
    const fields = isAdd ? tab.insertFields || [] : tab.editableFields || [];
    const idSuffix = formDialog.id
      ? " (id: " + formDialog.id + ")"
      : "";
    const dialogTitle = isAdd
      ? Strings("reversalAddRow") + " — " + tab.label
      : Strings("Edit") + " — " + tab.label + idSuffix;

    return (
      <Dialog
        open={formDialog.open}
        onClose={this.closeFormDialog}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: "bold" }}>
          {dialogTitle}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            "overflowX": "hidden",
            "& .row": {
              marginLeft: 0,
              marginRight: 0,
            },
          }}
        >
          <Row>
            {fields.map((field) => this.renderFormField(field))}
          </Row>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={this.closeFormDialog}
            disabled={actionLoading}
          >
            {Strings("Cancel")}
          </Button>

          <Button
            variant={isAdd ? "success" : "primary"}
            size='sm'
            onClick={this.submitForm}
            disabled={actionLoading}
            style={{ minWidth: 80 }}
          >
            {actionLoading ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              Strings("Save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderConfirmDialog = () => {
    const { confirmDialog, actionLoading } = this.state;
    return (
      <Dialog
        open={confirmDialog.open}
        onClose={this.closeDeleteDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle
          sx={{ fontSize: 15, fontWeight: "bold", color: "#c62828" }}
        >
          {Strings("confirmDelete")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            {Strings("reversalDeleteConfirm")}
          </DialogContentText>
          <div className='mt-2' style={{ fontWeight: "bold" }}>
            {confirmDialog.label}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant='outline-secondary'
            size='sm'
            onClick={this.closeDeleteDialog}
            disabled={actionLoading}
          >
            {Strings("Cancel")}
          </Button>
          <Button
            variant='danger'
            size='sm'
            onClick={this.confirmDelete}
            disabled={actionLoading}
            style={{ minWidth: 80 }}
          >
            {actionLoading ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              Strings("DELETE")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderInfoDialog = () => {
    const { infoDialog } = this.state;
    if (!infoDialog.rowData) {
      return null;
    }
    const entries = Object.entries(infoDialog.rowData);
    return (
      <Dialog
        open={infoDialog.open}
        onClose={this.closeInfo}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: "bold" }}>
          {Strings("rowDetails")}
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <StyledHeadCell sx={{ width: "35%" }}>
                    {Strings("fieldLabel")}
                  </StyledHeadCell>
                  <StyledHeadCell>
                    {Strings("valueLabel")}
                  </StyledHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(([k, v]) => (
                  <StyledRow key={k}>
                    <StyledBodyCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: 11,
                        color: "#893f3f",
                      }}
                    >
                      {k}
                    </StyledBodyCell>
                    <StyledBodyCell
                      sx={{ fontSize: 11, wordBreak: "break-all" }}
                    >
                      {formatValue(v)}
                    </StyledBodyCell>
                  </StyledRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size='sm'
            variant='outline-secondary'
            onClick={this.closeInfo}
          >
            {Strings("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { activeTab } = this.state;

    return (
      <div className='pb-12 paymentReversal duplicateEntryManagement'>
        <div className='headerText'>
          {Strings("PaymentReversal")}
        </div>

        <Row className='paper'>
          <Tabs
            selectedIndex={activeTab}
            onSelect={this.handleTabChange}
          >
            <TabList>
              {TABS.map((tab, i) => (
                <Tab
                  key={tab.key}
                  className={
                    activeTab === i
                      ? "react-tabs__tab react-tabs__tab--selected mt-1"
                      : "react-tabs__tab mt-1"
                  }
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>

            {TABS.map((tab) => (
              <TabPanel key={tab.key}>
                {this.renderNote(tab)}
                <Col md={12} className='pb-2 pt-2'>
                  {this.renderSearchFilter()}
                </Col>
                {this.renderMessages()}
                <Col md={12} className='ps-0 pe-0 pb-2 pt-2'>
                  <hr className='newhr opacity-100' />
                </Col>
                <Col md={12}>{this.renderTabContent(tab)}</Col>
              </TabPanel>
            ))}
          </Tabs>
        </Row>

        {this.renderConfirmDialog()}
        {this.renderFormDialog()}
        {this.renderInfoDialog()}
      </div>
    );
  }
}

export default PaymentReversal;
